import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Numbers as NumbersIcon,
  Abc as AbcIcon,
  CalendarMonth as CalendarIcon,
  DataObject as ObjectIcon,
  ErrorOutline as ErrorIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import type { TransformationTestResponse, HealthIssue } from '../services/api';

export type PreviewSampleTablesProps = {
  sampleOutput?: TransformationTestResponse['sample_output'];
  violations?: HealthIssue[];
};

// --- Helpers ---

const getTypeIcon = (value: unknown) => {
  if (typeof value === 'number') return <NumbersIcon fontSize="inherit" />;
  if (value instanceof Date) return <CalendarIcon fontSize="inherit" />;
  if (typeof value === 'object' && value !== null) return <ObjectIcon fontSize="inherit" />;
  return <AbcIcon fontSize="inherit" />;
};

const NULL_PLACEHOLDER = '—';

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return NULL_PLACEHOLDER;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

// Simple Quality Stats Calculation
const calculateStats = (rows: Array<Record<string, unknown>>, column: string) => {
  const total = rows.length;
  if (!total) return { valid: 0, nulls: 0, validPct: 0, nullPct: 0 };
  
  let nulls = 0;
  rows.forEach(row => {
    const val = row[column];
    if (val === null || val === undefined || val === '') {
      nulls++;
    }
  });
  
  const valid = total - nulls;
  return {
    valid,
    nulls,
    validPct: (valid / total) * 100,
    nullPct: (nulls / total) * 100,
  };
};

const DataQualityBar: React.FC<{ validPct: number; nullPct: number }> = ({ validPct, nullPct }) => {
  return (
    <Tooltip title={`Valid: ${validPct.toFixed(1)}%, Null: ${nullPct.toFixed(1)}%`}>
      <Box sx={{ display: 'flex', height: 4, width: '100%', bgcolor: '#eee', borderRadius: 1, overflow: 'hidden', mt: 0.5 }}>
        <Box sx={{ width: `${validPct}%`, bgcolor: 'success.main' }} />
        <Box sx={{ width: `${nullPct}%`, bgcolor: 'error.light' }} />
      </Box>
    </Tooltip>
  );
};

// --- Violation Helpers ---

type ViolationIndex = Map<string, HealthIssue[]>; // key = "table.column"

const buildViolationIndex = (violations: HealthIssue[] | undefined): ViolationIndex => {
  const idx = new Map<string, HealthIssue[]>();
  if (!violations) return idx;
  for (const v of violations) {
    if (!v.target_field) continue;
    const key = `${v.target_table || ''}.${v.target_field}`;
    const arr = idx.get(key) || [];
    arr.push(v);
    idx.set(key, arr);
  }
  return idx;
};

const buildCellViolationSet = (violations: HealthIssue[] | undefined, tableName: string): Map<string, Set<number>> => {
  // Returns Map<column, Set<rowIndex>> for cells that should be highlighted
  const cellMap = new Map<string, Set<number>>();
  if (!violations) return cellMap;
  for (const v of violations) {
    if (!v.target_field || !v.row_indices?.length) continue;
    if (v.target_table && v.target_table.toLowerCase() !== tableName.toLowerCase()) continue;
    const existing = cellMap.get(v.target_field) || new Set<number>();
    for (const ri of v.row_indices) existing.add(ri);
    cellMap.set(v.target_field, existing);
  }
  return cellMap;
};

const getCellSeverity = (violations: HealthIssue[] | undefined, tableName: string, column: string, rowIndex: number): 'error' | 'warning' | null => {
  if (!violations) return null;
  let severity: 'error' | 'warning' | null = null;
  for (const v of violations) {
    if (!v.target_field || v.target_field !== column) continue;
    if (v.target_table && v.target_table.toLowerCase() !== tableName.toLowerCase()) continue;
    if (v.row_indices && v.row_indices.includes(rowIndex)) {
      if (v.severity === 'error') return 'error';
      if (v.severity === 'warning') severity = 'warning';
    }
  }
  return severity;
};

const SEVERITY_BG = {
  error: 'rgba(239, 68, 68, 0.08)',
  warning: 'rgba(245, 158, 11, 0.08)',
};

const ColumnViolationBadge: React.FC<{ issues: HealthIssue[] }> = ({ issues }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const errorCount = issues.filter(i => i.severity === 'error').length;
  const warningCount = issues.filter(i => i.severity === 'warning').length;
  const color = errorCount > 0 ? '#ef4444' : '#f59e0b';
  const Icon = errorCount > 0 ? ErrorIcon : WarningIcon;

  return (
    <>
      <Chip
        size="small"
        icon={<Icon sx={{ fontSize: 12, color: `${color} !important` }} />}
        label={`${errorCount + warningCount}`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          height: 18,
          fontSize: 10,
          fontWeight: 600,
          bgcolor: `${color}14`,
          color,
          border: `1px solid ${color}33`,
          cursor: 'pointer',
          '& .MuiChip-icon': { ml: 0.3 },
        }}
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, maxWidth: 320, borderRadius: 2 } }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Validation Issues ({issues.length})
        </Typography>
        {issues.map((issue, idx) => (
          <Box key={idx} sx={{ mb: idx < issues.length - 1 ? 0.75 : 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {issue.severity === 'error'
                ? <ErrorIcon sx={{ fontSize: 12, color: '#ef4444' }} />
                : <WarningIcon sx={{ fontSize: 12, color: '#f59e0b' }} />}
              <Typography variant="caption" color="text.secondary">
                {issue.rule_type || issue.category}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', ml: 2 }}>
              {issue.message}
            </Typography>
            {issue.sample_violations && issue.sample_violations.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2, fontFamily: 'monospace', fontSize: 10 }}>
                e.g. {issue.sample_violations.slice(0, 2).map(String).join(', ')}
              </Typography>
            )}
          </Box>
        ))}
      </Popover>
    </>
  );
};

const deriveColumns = (rows: Array<Record<string, unknown>>): string[] => {
  const unique = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => unique.add(key));
  });
  return Array.from(unique);
};

const PreviewSampleTables: React.FC<PreviewSampleTablesProps> = ({ sampleOutput, violations }) => {
  const entries = useMemo(() => Object.entries(sampleOutput || {}), [sampleOutput]);
  const violationIndex = useMemo(() => buildViolationIndex(violations), [violations]);

  if (!entries.length) {
    return <Alert severity="info" sx={{ mt: 1 }}>Preview completed but no sample rows were returned.</Alert>;
  }

  return (
    <Stack spacing={4} sx={{ mt: 2 }}>
      {entries.map(([tableName, rows]) => {
        const safeRows = Array.isArray(rows) ? rows : [];
        const columns = deriveColumns(safeRows);

        return (
          <Box key={tableName} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {/* Header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: 'background.default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={600}>{tableName}</Typography>
                <Chip size="small" label="Preview" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {safeRows.length} sample row{safeRows.length !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {!safeRows.length ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No rows returned for this table.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 400, paddingRight: 1, scrollbarGutter: 'stable' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => {
                        const stats = calculateStats(safeRows, column);
                        // Infer type from first non-null value
                        const sampleVal = safeRows.find(r => r[column] !== null && r[column] !== undefined)?.[column];
                        const colIssues = violationIndex.get(`${tableName}.${column}`) || [];

                        return (
                          <TableCell key={column} sx={{ minWidth: 120, bgcolor: 'background.paper' }}>
                            <Box>
                                <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                                  <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                                    {getTypeIcon(sampleVal)}
                                  </Box>
                                  <Typography variant="caption" fontWeight={600} color="text.primary" sx={{ flex: 1 }}>
                                    {column}
                                  </Typography>
                                  {colIssues.length > 0 && (
                                    <ColumnViolationBadge issues={colIssues} />
                                  )}
                                </Stack>
                                <DataQualityBar validPct={stats.validPct} nullPct={stats.nullPct} />
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safeRows.map((row, rowIndex) => (
                      <TableRow key={rowIndex} hover>
                        {columns.map((column) => {
                          const cellSeverity = getCellSeverity(violations, tableName, column, rowIndex);
                          const rawValue = (row || {})[column];
                          const cellValue = formatCellValue(rawValue);
                          const isNull = rawValue === null || rawValue === undefined;
                          const tooltipTitle = cellSeverity
                            ? `${cellSeverity === 'error' ? 'Error' : 'Warning'}: value violates schema rule — ${cellValue}`
                            : isNull
                              ? 'NULL'
                              : cellValue;
                          return (
                            <TableCell
                              key={column}
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: 12,
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                maxWidth: 220,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                ...(cellSeverity && { bgcolor: SEVERITY_BG[cellSeverity] }),
                                ...(isNull && { color: 'text.disabled', fontStyle: 'italic' }),
                              }}
                            >
                              <Tooltip title={tooltipTitle} placement="top" arrow>
                                <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {cellValue}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {/* Footer with Stat Legend */}
            <Box sx={{ p: 1, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
               <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                  <Typography variant="caption" color="text.secondary">Valid</Typography>
               </Stack>
               <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.light' }} />
                  <Typography variant="caption" color="text.secondary">Null/Empty</Typography>
               </Stack>
               <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {NULL_PLACEHOLDER} = NULL
               </Typography>
               {violations && violations.length > 0 && (
                 <>
                   <Stack direction="row" spacing={1} alignItems="center">
                     <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: SEVERITY_BG.error }} />
                     <Typography variant="caption" color="text.secondary">Rule Error</Typography>
                   </Stack>
                   <Stack direction="row" spacing={1} alignItems="center">
                     <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: SEVERITY_BG.warning }} />
                     <Typography variant="caption" color="text.secondary">Rule Warning</Typography>
                   </Stack>
                 </>
               )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default PreviewSampleTables;
